/**
 * Pure routing for a live `UAFunctionValue` WS message: decides WHICH store
 * slot it targets and WHAT patch to apply — without touching any store. The
 * hook (use-websocket-lads) just applies the returned action. Extracted so the
 * filtering + key resolution is unit-testable (the React hook isn't).
 *
 * Key invariant: unit-store updates use `ctx.unitKey` (= the canonical
 * `unit.id` = `${deviceId}/${browsePath}`) 1:1. Re-prefixing it would
 * double-scope and write to a phantom key nobody reads.
 */
import { formatRawValue } from "../util/format-value";
import {
  matchChannelByBrowsePath,
  type UAFunctionValueMessage,
} from "../util/websocket-lads";
import type { ActiveProgram, DeviceChannels, Unit } from "../view-model";

export interface WsUpdateContext {
  /** Parent (viewing) device browseName; updates for other devices are dropped. */
  parentDeviceId?: string;
  /** Canonical unit-store key for this slot (unit.id = `${deviceId}/${browsePath}`). */
  unitKey?: string;
  /** Current channels for this slot. */
  channels: DeviceChannels;
  /** Metadata lookup (unit/precision) by deviceId + functionName. */
  getMetadata: (
    deviceId: string,
    functionName: string
  ) => { unit?: string; valuePrecision?: number } | undefined;
}

export type WsUpdateAction =
  | { type: "ignore" }
  | { type: "unit-state"; unitKey: string; state: Unit["state"] }
  | { type: "unit-program"; unitKey: string; program: Partial<ActiveProgram> }
  | { type: "channels"; channelKey: string; channels: DeviceChannels };

const PROGRAM_FIELDS: Record<string, (v: unknown) => Partial<ActiveProgram>> = {
  currentprogramtemplate: (v) => ({ currentProgramTemplate: String(v) }),
  currentruntime: (v) => ({ currentRuntime: Number(v) }),
  currentstepname: (v) => ({ currentStepName: String(v) }),
  currentstepnumber: (v) => ({ currentStepNumber: Number(v) }),
  currentstepruntime: (v) => ({ currentStepRuntime: Number(v) }),
  estimatedruntime: (v) => ({ estimatedRuntime: Number(v) }),
  estimatedstepnumbers: (v) => ({ estimatedStepNumbers: Number(v) }),
  estimatedstepruntime: (v) => ({ estimatedStepRuntime: Number(v) }),
  currentpausetime: (v) => ({ currentPauseTime: Number(v) }),
  deviceprogramrunid: (v) => ({ deviceProgramRunId: String(v) }),
};

const VALUE_TYPES = new Set([
  "sensorvalue",
  "currentvalue",
  "targetvalue",
  "isenabled",
  // Physischer Port eines modularen Sensors (essentim spider/sphere) — beim
  // Umstecken (Port 1 → 3) muss die Kachel live umgruppiert werden, nicht erst
  // nach Reload.
  "port",
  // Function-eigene State-Machine (CoverState/CurrentState, LCU-865) —
  // function-flach publiziert, wird als Channel-Wert behandelt.
  "currentstate",
]);

export function resolveWsUpdate(
  message: UAFunctionValueMessage,
  ctx: WsUpdateContext
): WsUpdateAction {
  const {
    browsePath,
    data,
    deviceId,
    functionName,
    unitId: messageUnitId,
    variableType,
  } = message;

  // Drop updates for another device.
  if (ctx.parentDeviceId && deviceId !== ctx.parentDeviceId) {
    return { type: "ignore" };
  }

  // Drop updates for another FU. Compare the message's FU segment against the
  // last segment of this slot's unitKey — guards against an organized
  // cross-device FU bleeding into the host's same-named slot.
  const slotUnitName = ctx.unitKey?.split("/").pop() ?? "";
  if (slotUnitName && messageUnitId && messageUnitId !== slotUnitName) {
    return { type: "ignore" };
  }

  // ControlFunction state → the owning channel's controlFunctionState. The WS
  // path is ".../FunctionSet/<fn>/ControlFunctionState/CurrentState"; drop the
  // ControlFunctionState segment so it matches the function-level channel.
  if (
    variableType === "CurrentState" &&
    browsePath?.includes("/ControlFunctionState/")
  ) {
    const channelKey = matchChannelByBrowsePath(
      ctx.channels,
      browsePath.replace("/ControlFunctionState/", "/")
    );
    if (!channelKey) return { type: "ignore" };
    const channels = {
      ...ctx.channels,
      [channelKey]: {
        ...ctx.channels[channelKey],
        controlFunctionState: String(data.value ?? "").toLowerCase(),
      },
    };
    return { type: "channels", channelKey, channels };
  }

  // FunctionalUnitState/CurrentState → unit state. NUR für den FU-Wrapper —
  // ein CurrentState unter FunctionSet (Cover-Function, LCU-865) ist ein
  // Channel-Wert und läuft unten durch den value-Pfad.
  if (
    variableType === "CurrentState" &&
    browsePath?.includes("/FunctionalUnitState")
  ) {
    if (!ctx.unitKey) return { type: "ignore" };
    const state = (
      String(data.value) || "stopped"
    ).toLowerCase() as Unit["state"];
    return { type: "unit-state", unitKey: ctx.unitKey, state };
  }

  // ProgramManager/ActiveProgram/* → active-program patch.
  const programMapper = PROGRAM_FIELDS[variableType.toLowerCase()];
  if (programMapper) {
    const unitKey = ctx.unitKey ?? deviceId;
    if (!unitKey) return { type: "ignore" };
    return {
      type: "unit-program",
      unitKey,
      program: programMapper(data.value),
    };
  }

  // Function value → channel patch.
  const vtLower = variableType.toLowerCase();
  if (!VALUE_TYPES.has(vtLower)) return { type: "ignore" };
  if (!ctx.channels || Object.keys(ctx.channels).length === 0) {
    return { type: "ignore" };
  }
  const channelKey = matchChannelByBrowsePath(ctx.channels, browsePath);
  if (!channelKey) return { type: "ignore" };

  const timestamp = data.serverTimestamp || data.sourceTimestamp;
  const metadata = ctx.getMetadata(deviceId, functionName);
  const unit = metadata?.unit;
  const precision =
    metadata?.valuePrecision ?? ctx.channels[channelKey]?.valuePrecision ?? 1;
  const formattedValue = formatRawValue(data.value, precision);
  const ts = timestamp ? new Date(timestamp) : new Date();

  const channels = { ...ctx.channels };
  switch (vtLower) {
    case "isenabled": {
      channels[channelKey] = {
        ...channels[channelKey],
        isEnabled: !!data.value,
      };

      break;
    }
    case "targetvalue": {
      channels[channelKey] = {
        ...channels[channelKey],
        ...(unit && { unit }),
        targetValue: {
          channel: channelKey,
          value: formattedValue,
          timestamp: ts,
        },
      };

      break;
    }
    case "port": {
      // Modular sensor moved to another physical port — update the channel's port
      // so the "(Port N)" grouping re-renders live.
      channels[channelKey] = {
        ...channels[channelKey],
        port: String(data.value),
      };

      break;
    }
    default: {
      channels[channelKey] = {
        ...channels[channelKey],
        ...(unit && { unit }),
        current: {
          value: formattedValue,
          timestamp: ts,
          statusCode: data.statusCode,
        },
      };
    }
  }
  return { type: "channels", channelKey, channels };
}
